// Inside Discord Activity: URL mappings proxy /api → epsteinexposed.com/api
// Standalone browser: use absolute URL (API has CORS Access-Control-Allow-Origin: *)
const isDiscordActivity = new URLSearchParams(window.location.search).has('frame_id')
  || new URLSearchParams(window.location.search).has('instance_id');
const API_BASE = isDiscordActivity
  ? '/api/v2'
  : 'https://epsteinexposed.com/api/v2';

const SITE_BASE = isDiscordActivity ? '' : 'https://epsteinexposed.com';

/** Resolve relative image URLs to absolute URLs pointing to the main site */
export function resolveImageUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${SITE_BASE}${url}`;
}

// ---------- Types ----------

export interface Person {
  id: string;
  slug: string;
  name: string;
  category: string;
  aliases: string[];
  short_bio: string;
  image_url: string | null;
  status: string | null;
  black_book_entry: boolean;
  stats: { documents: number; flights: number; connections: number; emails: number };
  url: string;
}

export interface PersonDetail extends Person {
  bio: string;
  nationality: string;
  notable_positions: string[];
  black_book_phones: number;
  tags: string[];
}

export interface Document {
  id: string;
  title: string;
  summary: string;
  date: string;
  source: string;
  category: string;
  tags: string[];
  pdf_url: string | null;
  source_url: string | null;
  page_count: number;
  bates_range: string | null;
  person_count: number;
  upvotes: number;
  url: string;
}

export interface Flight {
  id: string;
  date: string;
  origin: string;
  destination: string;
  aircraft: string;
  pilot: string;
  passenger_count: number;
  passenger_ids: string[];
  passenger_names: string[];
  url: string;
}

export interface Email {
  id: string;
  subject: string;
  snippet: string;
  rank: number;
  url: string;
}

export interface Connection {
  target_id: string;
  target_name: string;
  target_slug: string;
  strength: 'strong' | 'medium' | 'weak';
  weight: number;
  co_flights: number;
  co_documents: number;
  relationship_type: string;
  relationship_label: string;
  summary: string;
  url: string;
}

export interface GraphNode {
  id: string;
  name: string;
  category: string;
  slug: string;
  url: string;
}

export interface GraphEdge {
  source: string;
  target: string;
  strength: string;
  co_flights: number;
  co_documents: number;
  relationship_type: string;
}

export interface GraphData {
  nodes: GraphNode[];
  edges: GraphEdge[];
  summary: { node_count: number; edge_count: number; min_shared_docs: number };
}

export interface Location {
  id: string;
  slug: string;
  name: string;
  type: string;
  description: string;
  address: string;
  coordinates: { lat: number; lng: number } | null;
  image_url: string | null;
  url: string;
}

export interface FinancialFlow {
  id: string;
  source_entity: string;
  target_entity: string;
  source_name: string;
  target_name: string;
  amount: number;
  amount_display: string;
  currency: string;
  date_start: string;
  date_end: string;
  flow_type: string;
  flow_direction: string;
  payment_type: string;
  description: string;
  confidence: number;
  confidence_tier: string;
  bank: string;
}

export interface FinancialStats {
  total_tracked: number;
  total_tracked_display: string;
  entity_count: number;
  flow_count: number;
  shell_company_count: number;
  bank_count: number;
}

export interface FinancialEntity {
  id: string;
  name: string;
  slug: string;
  entity_type: string;
  total_inflow: number;
  total_outflow: number;
  is_shell: boolean;
}

export interface TimelineEvent {
  id: string;
  date: string;
  type: string;
  title: string;
  description: string;
  amount: number | null;
  amount_display: string | null;
  person_ids: string[];
  url: string;
}

export interface SiteStats {
  counts: {
    persons: number;
    documents: number;
    flights: number;
    emails: number;
    locations: number;
    connections: number;
  };
}

export interface PaginatedMeta {
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

export interface ApiResponse<T> {
  api_version: string;
  status: string;
  data: T;
  meta: PaginatedMeta;
}

export interface SearchResult {
  query: string;
  type: string;
  documents: { total: number; items: Array<{ id: string; title: string; snippet: string; rank: number; url: string }> };
  emails: { total: number; items: Array<{ id: string; subject: string; snippet: string; rank: number; url: string }> };
}

// ---------- Fetch Helpers ----------

async function apiFetch<T>(path: string, params?: Record<string, string | number | boolean | undefined>): Promise<ApiResponse<T>> {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    }
  }
  const res = await fetch(url.toString());
  if (!res.ok) {
    throw new Error(`API ${res.status}: ${path}`);
  }
  return res.json();
}

// ---------- Persons ----------

export async function fetchPersons(params?: {
  q?: string; category?: string; black_book?: boolean;
  sort?: string; page?: number; per_page?: number;
}) {
  return apiFetch<Person[]>('/persons', params as any);
}

export async function fetchPerson(slug: string) {
  return apiFetch<PersonDetail>(`/persons/${slug}`);
}

export async function fetchPersonConnections(slug: string, params?: { min_shared_docs?: number; limit?: number }) {
  return apiFetch<Connection[]>(`/persons/${slug}/connections`, params as any);
}

export async function fetchPersonDocuments(slug: string, params?: { page?: number; per_page?: number }) {
  return apiFetch<Document[]>(`/persons/${slug}/documents`, params as any);
}

export async function fetchPersonFlights(slug: string) {
  return apiFetch<Flight[]>(`/persons/${slug}/flights`);
}

// ---------- Documents ----------

export async function fetchDocuments(params?: {
  q?: string; category?: string; source?: string; has_pdf?: boolean;
  person_id?: string; date_from?: string; date_to?: string;
  sort?: string; page?: number; per_page?: number;
}) {
  return apiFetch<Document[]>('/documents', params as any);
}

export async function fetchDocument(id: string) {
  return apiFetch<Document>(`/documents/${id}`);
}

// ---------- Search ----------

export async function fetchSearch(params: { q: string; type?: string; limit?: number; offset?: number }) {
  return apiFetch<SearchResult>('/search', params as any);
}

// ---------- Flights ----------

export async function fetchFlights(params?: {
  passenger?: string; passenger_id?: string; year?: string;
  origin?: string; destination?: string; page?: number; per_page?: number;
}) {
  return apiFetch<Flight[]>('/flights', params as any);
}

// ---------- Network ----------

export async function fetchNetworkGraph(minSharedDocs = 20) {
  return apiFetch<GraphData>('/network/graph', { min_shared_docs: minSharedDocs });
}

export async function fetchNetworkPath(from: string, to: string) {
  return apiFetch<any>('/network/path', { from, to });
}

// ---------- Financial ----------

export async function fetchFinancialFlows(params?: {
  entity?: string; type?: string; direction?: string;
  payment_type?: string; bank?: string; page?: number; per_page?: number;
}) {
  return apiFetch<FinancialFlow[]>('/financial', { view: 'flows', ...params } as any);
}

export async function fetchFinancialStats() {
  return apiFetch<FinancialStats>('/financial', { view: 'stats' });
}

export async function fetchFinancialEntities(params?: { page?: number; per_page?: number }) {
  return apiFetch<FinancialEntity[]>('/financial', { view: 'entities', ...params } as any);
}

// ---------- Locations ----------

export async function fetchLocations() {
  return apiFetch<Location[]>('/locations');
}

// ---------- Stats ----------

export async function fetchStats() {
  return apiFetch<SiteStats>('/stats');
}

// ---------- Timeline ----------

export async function fetchTimeline(params?: {
  person?: string; type?: string; date_from?: string; date_to?: string;
  page?: number; per_page?: number;
}) {
  return apiFetch<TimelineEvent[]>('/timeline', params as any);
}

// ---------- Emails ----------

export async function fetchEmails(params: { q: string; page?: number; per_page?: number }) {
  return apiFetch<Email[]>('/emails', params as any);
}
